import * as THREE from "three";
import {
  createPositionGizmo,
  disposePositionGizmo,
  getAxisWorldDirection,
  setDraggingAxis,
  setHoveredAxis,
  updateGizmoTransform,
  type AxisIndex,
  type PositionGizmo,
} from "./positionGizmoObject";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { getHistoryActions } from "../stores/historyStore";
import { logger } from "../utils/logger";
import {
  getSavedCameraTranslationGizmoQuaternion,
  getSavedCameraWorldPose,
} from "../utils/savedCameraWorldPose";
import { commitBodyTransform } from "../viewer/utils/commitTransform";
import type { BodyUUID, CameraUUID, SiteUUID } from "../types/primitives";
import {
  buildParentMapFromSceneState,
  filterTopmostBodies,
  captureMultiBodySnapshots,
} from "./multiBodyTransformUtils";
import {
  closestPointParamOnLineToRay,
  computeBodyWorldMatrix,
  computeParentWorldMatrix,
} from "./positionGizmoMath";
import {
  captureMultiGeomPositionSnapshots,
  computeMultiGeomLocalCentroid,
} from "./positionGizmoMultiGeom";
import { dispatchPositionGizmoDragEvent, setNdcPointerFromEvent } from "./positionGizmoDom";
import {
  resolveSceneObjectForBody,
  resolveSceneObjectForTarget,
} from "./positionGizmoSceneObjects";
import type {
  AttachPositionGizmoOptions,
  PositionDragState,
  PositionGizmoController,
  SceneBodyLookup,
  SceneJointLookup,
  SceneSiteLookup,
} from "./positionGizmoTypes";

export type { AttachPositionGizmoOptions, PositionGizmoController } from "./positionGizmoTypes";

const AXIS_LETTERS: ["X", "Y", "Z"] = ["X", "Y", "Z"];

export function attachPositionGizmoController(
  opts: AttachPositionGizmoOptions
): PositionGizmoController {
  const {
    sceneRef,
    cameraRef,
    rendererRef,
    controlsRef,
    sceneObjectsRef,
    jointObjectsRef,
    getSelectedBodyUuid,
    isDisabled,
    isOrbiting,
  } = opts;

  const scene = sceneRef?.current;
  const renderer = rendererRef?.current;
  const camera = cameraRef?.current;
  if (!scene || !renderer || !camera) {
    return { frameUpdate: () => {}, detach: () => {} };
  }

  const gizmo: PositionGizmo = createPositionGizmo();
  scene.add(gizmo.root);

  const raycaster = new THREE.Raycaster();
  raycaster.layers.enableAll();
  const pointer = new THREE.Vector2();

  let hoveredAxis: AxisIndex | null = null;
  let dragState: PositionDragState | null = null;

  const rendererDom = renderer.domElement;

  const updateHover = (event: PointerEvent): AxisIndex | null => {
    if (!gizmo.root.visible) return null;
    setNdcPointerFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const intersects = raycaster.intersectObjects(gizmo.pickTargets, false);
    if (!intersects.length) return null;
    const hit = intersects[0];
    const axis = hit.object.userData?.positionGizmoAxis;
    if (typeof axis === "number") return axis as AxisIndex;
    return null;
  };

  const setHover = (axis: AxisIndex | null) => {
    if (hoveredAxis === axis) return;
    hoveredAxis = axis;
    setHoveredAxis(gizmo, hoveredAxis);
    if (!dragState) {
      rendererDom.style.cursor = axis == null ? "" : "grab";
    }
  };

  const onPointerMoveCanvas = (event: PointerEvent) => {
    if (dragState) return; // drag handled at window level
    if (isOrbiting()) return;
    if (isDisabled() || !gizmo.root.visible) {
      setHover(null);
      return;
    }
    const axis = updateHover(event);
    setHover(axis);
  };

  const onPointerLeaveCanvas = () => {
    if (!dragState) setHover(null);
  };

  const onPointerDownWindow = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const eventTarget = event.target as Node | null;
    // Only intercept when the pointerdown is on our canvas.
    if (!eventTarget || (eventTarget !== rendererDom && !rendererDom.contains(eventTarget))) {
      return;
    }
    if (isDisabled() || !gizmo.root.visible) return;
    const axis = updateHover(event);
    if (axis == null) return;
    const explicitTarget = opts.getSelectedTarget?.();

    const multiGeomTargets = captureMultiGeomPositionSnapshots(
      opts.getSelectedGeomTargets?.() ?? []
    );
    if (multiGeomTargets.length > 1 && (!explicitTarget || explicitTarget.kind === "geom")) {
      gizmo.root.updateMatrixWorld(true);
      const axisOriginWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
      const axisDirWorld = getAxisWorldDirection(gizmo, axis, new THREE.Vector3());
      setNdcPointerFromEvent(pointer, event, rendererDom);
      raycaster.setFromCamera(pointer, camera);
      const startParam = closestPointParamOnLineToRay(
        axisOriginWorld,
        axisDirWorld,
        raycaster.ray.origin,
        raycaster.ray.direction
      );

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      dragState = {
        axis,
        target: {
          kind: "geom" as const,
          bodyUuid: multiGeomTargets[0].bodyUuid,
          geomId: multiGeomTargets[0].geomId,
          object: multiGeomTargets[0].object,
        },
        startLocalPos: multiGeomTargets[0].startLocalPos,
        startQuat: null,
        axisOriginWorld,
        axisDirWorld,
        startParam,
        startedHistoryGroup: false,
        pointerId: event.pointerId,
        multiGeomTargets,
      };
      setDraggingAxis(gizmo, axis);
      try {
        getHistoryActions().beginGroup(getSceneState());
        dragState.startedHistoryGroup = true;
      } catch (error) {
        logger.warn("[PositionGizmo] Failed to begin history group", error);
      }
      if (controlsRef?.current) controlsRef.current.enabled = false;
      rendererDom.style.cursor = "grabbing";
      try {
        rendererDom.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      window.addEventListener("pointermove", onPointerMoveWindow, true);
      window.addEventListener("pointerup", onPointerUpWindow, true);
      window.addEventListener("pointercancel", onPointerUpWindow, true);
      dispatchPositionGizmoDragEvent("position-gizmo-drag", {
        axis,
        axisLetter: AXIS_LETTERS[axis],
        localValue: multiGeomTargets[0].startLocalPos[axis],
        clientX: event.clientX,
        clientY: event.clientY,
        bodyUuid: multiGeomTargets[0].bodyUuid,
      });
      return;
    }

    // Multi-body drag: intercept when multiple bodies are selected and no specific sub-target.
    const selectedBodies = opts.getSelectedBodies?.() ?? [];
    if (selectedBodies.length > 1 && (!explicitTarget || explicitTarget.kind === "body")) {
      const sceneObjs = sceneObjectsRef?.current;
      if (!sceneObjs) return;
      const parentMap = buildParentMapFromSceneState();
      const topmost = filterTopmostBodies(selectedBodies, parentMap);
      const snapshots = captureMultiBodySnapshots(topmost, sceneObjs);
      if (!snapshots.length) return;

      gizmo.root.updateMatrixWorld(true);
      const axisOriginWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
      const axisDirWorld = getAxisWorldDirection(gizmo, axis, new THREE.Vector3());
      setNdcPointerFromEvent(pointer, event, rendererDom);
      raycaster.setFromCamera(pointer, camera);
      const startParam = closestPointParamOnLineToRay(
        axisOriginWorld,
        axisDirWorld,
        raycaster.ray.origin,
        raycaster.ray.direction
      );

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      dragState = {
        axis,
        target: { kind: "body" as const, bodyUuid: topmost[0] },
        startLocalPos: snapshots[0].startLocalPos,
        startQuat: snapshots[0].startQuat,
        axisOriginWorld,
        axisDirWorld,
        startParam,
        startedHistoryGroup: false,
        pointerId: event.pointerId,
        multiBodyTargets: snapshots,
      };
      setDraggingAxis(gizmo, axis);
      try {
        getHistoryActions().beginGroup(getSceneState());
        dragState.startedHistoryGroup = true;
      } catch (error) {
        logger.warn("[PositionGizmo] Failed to begin history group", error);
      }
      if (controlsRef?.current) controlsRef.current.enabled = false;
      rendererDom.style.cursor = "grabbing";
      try {
        rendererDom.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      window.addEventListener("pointermove", onPointerMoveWindow, true);
      window.addEventListener("pointerup", onPointerUpWindow, true);
      window.addEventListener("pointercancel", onPointerUpWindow, true);
      dispatchPositionGizmoDragEvent("position-gizmo-drag", {
        axis,
        axisLetter: AXIS_LETTERS[axis],
        localValue: snapshots[0].startLocalPos[axis],
        clientX: event.clientX,
        clientY: event.clientY,
        bodyUuid: topmost[0],
      });
      return;
    }

    const fallbackBodyUuid = getSelectedBodyUuid();
    const target = explicitTarget
      ? explicitTarget
      : fallbackBodyUuid
        ? { kind: "body" as const, bodyUuid: fallbackBodyUuid }
        : null;
    if (!target) return;
    if (target.kind !== "camera") {
      const sceneObj = resolveSceneObjectForTarget(sceneObjectsRef?.current, target);
      if (!sceneObj) return;
    }
    const robotActions = getSceneActions();
    const bodyUuidBranded = target.kind !== "camera" ? (target.bodyUuid as BodyUUID) : undefined;
    let startLocalPos: [number, number, number];
    let startQuat: [number, number, number, number] | null = null;
    if (target.kind === "camera") {
      const cam = (
        getSceneState().cameras as Record<string, { position?: number[] } | undefined>
      )?.[target.cameraUuid];
      const camPos = Array.isArray(cam?.position) ? cam.position : [0, 0.5, 2];
      startLocalPos = [Number(camPos[0] ?? 0), Number(camPos[1] ?? 0), Number(camPos[2] ?? 0)];
    } else if (target.kind === "joint") {
      const jointData = (getSceneState().joints as SceneJointLookup | undefined)?.[
        target.jointUuid
      ];
      const jointPos = Array.isArray(jointData?.position) ? jointData.position : [0, 0, 0];
      startLocalPos = [
        Number(jointPos[0] ?? 0),
        Number(jointPos[1] ?? 0),
        Number(jointPos[2] ?? 0),
      ];
    } else if (target.kind === "geom") {
      const body = (getSceneState().bodies as SceneBodyLookup | undefined)?.[target.bodyUuid];
      const geom = Array.isArray(body?.geoms)
        ? body.geoms.find((entry) => String(entry?.uuid) === String(target.geomId))
        : null;
      const geomPos = Array.isArray(geom?.position) ? geom.position : [0, 0, 0];
      startLocalPos = [Number(geomPos[0] ?? 0), Number(geomPos[1] ?? 0), Number(geomPos[2] ?? 0)];
    } else if (target.kind === "site") {
      const siteData = (getSceneState().sites as SceneSiteLookup | undefined)?.[target.siteUuid];
      const sitePos = Array.isArray(siteData?.position) ? siteData.position : [0, 0, 0];
      startLocalPos = [Number(sitePos[0] ?? 0), Number(sitePos[1] ?? 0), Number(sitePos[2] ?? 0)];
    } else if (target.kind === "inertial") {
      const inertial = (getSceneState().bodies as SceneBodyLookup | undefined)?.[target.bodyUuid]
        ?.inertia;
      const inertialPos = Array.isArray(inertial?.position) ? inertial.position : [0, 0, 0];
      startLocalPos = [
        Number(inertialPos[0] ?? 0),
        Number(inertialPos[1] ?? 0),
        Number(inertialPos[2] ?? 0),
      ];
    } else {
      const bodyPos = (robotActions.getBodyPosition(bodyUuidBranded as BodyUUID) || [0, 0, 0]) as
        | number[]
        | [number, number, number];
      const bodyQuat = (robotActions.getBodyRotation(bodyUuidBranded as BodyUUID) || [
        1, 0, 0, 0,
      ]) as number[] | [number, number, number, number];
      startLocalPos = [Number(bodyPos[0] ?? 0), Number(bodyPos[1] ?? 0), Number(bodyPos[2] ?? 0)];
      startQuat = [
        Number(bodyQuat[0] ?? 1),
        Number(bodyQuat[1] ?? 0),
        Number(bodyQuat[2] ?? 0),
        Number(bodyQuat[3] ?? 0),
      ];
    }

    // Gizmo has been updated this frame already (from frameUpdate before input);
    // ensure world matrix is fresh just in case.
    gizmo.root.updateMatrixWorld(true);
    const axisOriginWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
    const axisDirWorld = getAxisWorldDirection(gizmo, axis, new THREE.Vector3());

    setNdcPointerFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const startParam = closestPointParamOnLineToRay(
      axisOriginWorld,
      axisDirWorld,
      raycaster.ray.origin,
      raycaster.ray.direction
    );

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    dragState = {
      axis,
      target,
      startLocalPos,
      startQuat,
      axisOriginWorld,
      axisDirWorld,
      startParam,
      startedHistoryGroup: false,
      pointerId: event.pointerId,
    };
    setDraggingAxis(gizmo, axis);
    try {
      getHistoryActions().beginGroup(getSceneState());
      dragState.startedHistoryGroup = true;
    } catch (error) {
      logger.warn("[PositionGizmo] Failed to begin history group", error);
    }

    if (controlsRef?.current) controlsRef.current.enabled = false;
    rendererDom.style.cursor = "grabbing";
    try {
      rendererDom.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }

    window.addEventListener("pointermove", onPointerMoveWindow, true);
    window.addEventListener("pointerup", onPointerUpWindow, true);
    window.addEventListener("pointercancel", onPointerUpWindow, true);

    dispatchPositionGizmoDragEvent("position-gizmo-drag", {
      axis,
      axisLetter: AXIS_LETTERS[axis],
      localValue: dragState.startLocalPos[axis],
      clientX: event.clientX,
      clientY: event.clientY,
      bodyUuid: target.kind === "camera" ? undefined : target.bodyUuid,
      cameraUuid: target.kind === "camera" ? target.cameraUuid : undefined,
      jointUuid: target.kind === "joint" ? target.jointUuid : undefined,
    });
  };

  const onPointerMoveWindow = (event: PointerEvent) => {
    if (!dragState) return;
    setNdcPointerFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const currentParam = closestPointParamOnLineToRay(
      dragState.axisOriginWorld,
      dragState.axisDirWorld,
      raycaster.ray.origin,
      raycaster.ray.direction
    );
    if (!Number.isFinite(currentParam)) return;

    const delta = currentParam - dragState.startParam;

    // Multi-body translate: apply world-space delta to each body's parent-local position.
    if (dragState.multiBodyTargets && dragState.multiBodyTargets.length > 0) {
      const worldDelta = dragState.axisDirWorld.clone().multiplyScalar(delta);
      for (const mb of dragState.multiBodyTargets) {
        const parentWorldInvQuat = mb.parentWorldQuat.clone().invert();
        const parentLocalDelta = worldDelta.clone().applyQuaternion(parentWorldInvQuat);
        const newLocalPos: [number, number, number] = [
          mb.startLocalPos[0] + parentLocalDelta.x,
          mb.startLocalPos[1] + parentLocalDelta.y,
          mb.startLocalPos[2] + parentLocalDelta.z,
        ];
        try {
          commitBodyTransform(getSceneActions(), mb.bodyUuid, newLocalPos, mb.startQuat);
        } catch (error) {
          logger.warn("[PositionGizmo] Failed to commit multi-body transform", error);
        }
      }
      return;
    }

    if (dragState.multiGeomTargets && dragState.multiGeomTargets.length > 0) {
      for (const geomTarget of dragState.multiGeomTargets) {
        const newLocal: [number, number, number] = [
          geomTarget.startLocalPos[0],
          geomTarget.startLocalPos[1],
          geomTarget.startLocalPos[2],
        ];
        newLocal[dragState.axis] = geomTarget.startLocalPos[dragState.axis] + delta;
        try {
          getSceneActions().setGeomPosition(
            geomTarget.bodyUuid as BodyUUID,
            geomTarget.geomId,
            newLocal
          );
          geomTarget.object?.position?.set(newLocal[0], newLocal[1], newLocal[2]);
        } catch (error) {
          logger.warn("[PositionGizmo] Failed to commit multi-geom transform", error);
        }
      }
      return;
    }

    // Gizmo is scaled; the parameter is in gizmo-local units (world = origin + t * dir,
    // dir is already a unit world vector). So the delta in *world distance* along the
    // axis is (currentParam - startParam). Because the parent frame is orthonormal
    // in world (rotations only), that world delta equals the parent-local delta.
    const newLocal: [number, number, number] = [
      dragState.startLocalPos[0],
      dragState.startLocalPos[1],
      dragState.startLocalPos[2],
    ];
    newLocal[dragState.axis] = dragState.startLocalPos[dragState.axis] + delta;

    try {
      if (dragState.target.kind === "joint") {
        getSceneActions().updateJointPos(dragState.target.jointUuid, newLocal);
      } else if (dragState.target.kind === "geom") {
        getSceneActions().setGeomPosition(
          dragState.target.bodyUuid as BodyUUID,
          dragState.target.geomId,
          newLocal
        );
        dragState.target.object?.position?.set(newLocal[0], newLocal[1], newLocal[2]);
      } else if (dragState.target.kind === "site") {
        getSceneActions().setSitePosition(dragState.target.siteUuid as SiteUUID, newLocal);
        dragState.target.object?.position?.set(newLocal[0], newLocal[1], newLocal[2]);
      } else if (dragState.target.kind === "inertial") {
        getSceneActions().updateInertia(dragState.target.bodyUuid as BodyUUID, {
          position: newLocal,
        });
        dragState.target.object?.position?.set(newLocal[0], newLocal[1], newLocal[2]);
      } else if (dragState.target.kind === "camera") {
        getSceneActions().updateCamera(dragState.target.cameraUuid as CameraUUID, {
          position: newLocal,
        });
      } else if (dragState.startQuat) {
        commitBodyTransform(
          getSceneActions(),
          dragState.target.bodyUuid,
          newLocal,
          dragState.startQuat
        );
      }
    } catch (error) {
      logger.warn("[PositionGizmo] Failed to commit transform", error);
    }

    dispatchPositionGizmoDragEvent("position-gizmo-drag", {
      axis: dragState.axis,
      axisLetter: AXIS_LETTERS[dragState.axis],
      localValue: newLocal[dragState.axis],
      clientX: event.clientX,
      clientY: event.clientY,
      bodyUuid: dragState.target.kind === "camera" ? undefined : dragState.target.bodyUuid,
      cameraUuid: dragState.target.kind === "camera" ? dragState.target.cameraUuid : undefined,
      jointUuid: dragState.target.kind === "joint" ? dragState.target.jointUuid : undefined,
    });
  };

  const onPointerUpWindow = (event: PointerEvent) => {
    if (!dragState) return;
    const endedGroup = dragState.startedHistoryGroup;
    const capturedPointerId = dragState.pointerId;
    const bodyUuid = dragState.target.kind === "camera" ? undefined : dragState.target.bodyUuid;
    const cameraUuid = dragState.target.kind === "camera" ? dragState.target.cameraUuid : undefined;
    const jointUuid = dragState.target.kind === "joint" ? dragState.target.jointUuid : undefined;
    const axis = dragState.axis;
    dragState = null;
    setDraggingAxis(gizmo, null);

    window.removeEventListener("pointermove", onPointerMoveWindow, true);
    window.removeEventListener("pointerup", onPointerUpWindow, true);
    window.removeEventListener("pointercancel", onPointerUpWindow, true);

    if (controlsRef?.current) controlsRef.current.enabled = true;
    rendererDom.style.cursor = hoveredAxis == null ? "" : "grab";
    if (capturedPointerId != null) {
      try {
        rendererDom.releasePointerCapture(capturedPointerId);
      } catch {
        // ignore
      }
    }

    if (endedGroup) {
      try {
        getHistoryActions().endGroup(getSceneState());
      } catch (error) {
        logger.warn("[PositionGizmo] Failed to end history group", error);
      }
    }

    dispatchPositionGizmoDragEvent("position-gizmo-drag-end", {
      axis,
      axisLetter: AXIS_LETTERS[axis],
      bodyUuid,
      cameraUuid,
      jointUuid,
      clientX: event?.clientX,
      clientY: event?.clientY,
    });
  };

  // Listen at window-capture so our handler runs BEFORE OrbitControls / scene
  // listeners on the canvas (capture phase on a window/document ancestor fires
  // before listeners on the target element, regardless of phase).
  window.addEventListener("pointerdown", onPointerDownWindow, true);
  rendererDom.addEventListener("pointermove", onPointerMoveCanvas);
  rendererDom.addEventListener("pointerleave", onPointerLeaveCanvas);

  const frameUpdate = () => {
    if (isDisabled()) {
      if (gizmo.root.visible) {
        gizmo.root.visible = false;
        setDraggingAxis(gizmo, null);
        setHover(null);
      }
      return;
    }

    // Multi-body mode: place gizmo at centroid of all selected bodies.
    const multiGeomTargets = captureMultiGeomPositionSnapshots(
      opts.getSelectedGeomTargets?.() ?? []
    );
    if (multiGeomTargets.length > 1) {
      const sceneObjs = sceneObjectsRef?.current;
      const bodyObj = sceneObjs?.[multiGeomTargets[0].bodyUuid];
      if (!bodyObj) {
        if (gizmo.root.visible) {
          gizmo.root.visible = false;
          setDraggingAxis(gizmo, null);
          setHover(null);
        }
        return;
      }
      const bodyWorldMat = computeBodyWorldMatrix(bodyObj);
      const bodyQuat = new THREE.Quaternion().setFromRotationMatrix(bodyWorldMat);
      const centroid = computeMultiGeomLocalCentroid(multiGeomTargets).applyMatrix4(bodyWorldMat);
      const cam = cameraRef?.current;
      let distance = 1;
      if (cam) {
        const camPos = new THREE.Vector3();
        cam.getWorldPosition(camPos);
        distance = camPos.distanceTo(centroid);
      }
      updateGizmoTransform(gizmo, centroid, bodyQuat, distance);
      if (!gizmo.root.visible) gizmo.root.visible = true;
      return;
    }

    // Multi-body mode: place gizmo at centroid of all selected bodies.
    const selectedBodies = opts.getSelectedBodies?.() ?? [];
    if (selectedBodies.length > 1) {
      const explicitTarget = opts.getSelectedTarget?.();
      if (!explicitTarget || explicitTarget.kind === "body") {
        const sceneObjs = sceneObjectsRef?.current;
        if (!sceneObjs) {
          if (gizmo.root.visible) {
            gizmo.root.visible = false;
            setDraggingAxis(gizmo, null);
            setHover(null);
          }
          return;
        }
        const centroid = new THREE.Vector3();
        let count = 0;
        for (const uuid of selectedBodies) {
          const obj = sceneObjs[uuid];
          if (!obj) continue;
          const wm = computeBodyWorldMatrix(obj);
          centroid.add(new THREE.Vector3().setFromMatrixPosition(wm));
          count++;
        }
        if (count === 0) {
          if (gizmo.root.visible) {
            gizmo.root.visible = false;
            setDraggingAxis(gizmo, null);
            setHover(null);
          }
          return;
        }
        centroid.divideScalar(count);
        const frameQuat = new THREE.Quaternion();
        const firstObj = sceneObjs[selectedBodies[0]];
        if (firstObj) {
          const parentWorldMat = computeParentWorldMatrix(firstObj);
          frameQuat.setFromRotationMatrix(parentWorldMat);
        }
        const cam = cameraRef?.current;
        let distance = 1;
        if (cam) {
          const camPos = new THREE.Vector3();
          cam.getWorldPosition(camPos);
          distance = camPos.distanceTo(centroid);
        }
        updateGizmoTransform(gizmo, centroid, frameQuat, distance);
        if (!gizmo.root.visible) gizmo.root.visible = true;
        return;
      }
    }

    const explicitTarget = opts.getSelectedTarget?.();
    const fallbackBodyUuid = getSelectedBodyUuid();
    const target = explicitTarget
      ? explicitTarget
      : fallbackBodyUuid
        ? { kind: "body" as const, bodyUuid: fallbackBodyUuid }
        : null;
    if (!target) {
      if (gizmo.root.visible) {
        gizmo.root.visible = false;
        setDraggingAxis(gizmo, null);
        setHover(null);
      }
      return;
    }

    if (target.kind === "camera") {
      const sceneState = getSceneState();
      const camDef = (
        sceneState.cameras as unknown as Record<string, Record<string, unknown> | undefined>
      )?.[target.cameraUuid];
      if (!camDef) {
        if (gizmo.root.visible) {
          gizmo.root.visible = false;
          setDraggingAxis(gizmo, null);
          setHover(null);
        }
        return;
      }
      const modelGroup = scene.getObjectByName("modelGroup") || null;
      const pose = getSavedCameraWorldPose(
        camDef,
        sceneObjectsRef?.current ?? {},
        sceneState.bodies ?? {},
        sceneState.sites ?? {},
        modelGroup
      );
      const origin = pose.position.clone();
      const translateFrameQuat = getSavedCameraTranslationGizmoQuaternion(
        camDef,
        sceneObjectsRef?.current,
        sceneState.bodies ?? {},
        sceneState.sites ?? {},
        modelGroup
      );
      const viewportCam = cameraRef?.current;
      let distance = 1;
      if (viewportCam) {
        const camPos = new THREE.Vector3();
        viewportCam.getWorldPosition(camPos);
        distance = camPos.distanceTo(origin);
      }

      updateGizmoTransform(gizmo, origin, translateFrameQuat, distance);
      if (!gizmo.root.visible) gizmo.root.visible = true;
      return;
    }

    const sceneObj = resolveSceneObjectForTarget(sceneObjectsRef?.current, target);
    if (!sceneObj) {
      if (gizmo.root.visible) {
        gizmo.root.visible = false;
        setDraggingAxis(gizmo, null);
        setHover(null);
      }
      return;
    }

    const targetWorldMat = computeBodyWorldMatrix(sceneObj);
    const bodyObj = resolveSceneObjectForBody(sceneObjectsRef?.current, target.bodyUuid);
    const bodyWorldMat = bodyObj ? computeBodyWorldMatrix(bodyObj) : targetWorldMat;
    const bodyQuat = new THREE.Quaternion().setFromRotationMatrix(bodyWorldMat);
    let origin = new THREE.Vector3().setFromMatrixPosition(targetWorldMat);
    const frameQuat = new THREE.Quaternion();
    if (target.kind === "joint") {
      const jointData = (getSceneState().joints as SceneJointLookup | undefined)?.[
        target.jointUuid
      ];
      const jointPos = Array.isArray(jointData?.position) ? jointData.position : [0, 0, 0];
      const jointLocal = new THREE.Vector3(
        Number(jointPos[0] ?? 0),
        Number(jointPos[1] ?? 0),
        Number(jointPos[2] ?? 0)
      );
      const jointRef = jointObjectsRef?.current?.[target.jointUuid] || null;
      if (jointRef?.bodyGroup) {
        const basePos = jointRef.initialPos || jointRef.bodyGroup.position || new THREE.Vector3();
        const baseRot =
          jointRef.initialRot || jointRef.bodyGroup.quaternion || new THREE.Quaternion();
        const parentObj =
          jointRef.bodyGroup.userData?._pivotGroup?.parent || jointRef.bodyGroup.parent;
        origin = basePos.clone().add(jointLocal.applyQuaternion(baseRot));
        if (parentObj?.localToWorld) parentObj.localToWorld(origin);
        const parentWorldQuat = new THREE.Quaternion();
        if (parentObj?.getWorldQuaternion) parentObj.getWorldQuaternion(parentWorldQuat);
        frameQuat.copy(parentWorldQuat.multiply(baseRot));
      } else {
        origin = jointLocal.applyMatrix4(bodyWorldMat);
        frameQuat.copy(bodyQuat);
      }
    } else if (target.kind === "body") {
      // Parent-local frame: gizmo axes align with the PARENT body's world rotation.
      const parentWorldMat = computeParentWorldMatrix(sceneObj);
      frameQuat.setFromRotationMatrix(parentWorldMat);
    } else {
      frameQuat.copy(bodyQuat);
    }

    const cam = cameraRef?.current;
    let distance = 1;
    if (cam) {
      const camPos = new THREE.Vector3();
      cam.getWorldPosition(camPos);
      distance = camPos.distanceTo(origin);
    }

    updateGizmoTransform(gizmo, origin, frameQuat, distance);
    if (!gizmo.root.visible) gizmo.root.visible = true;
  };

  const detach = () => {
    if (dragState) {
      try {
        onPointerUpWindow(new PointerEvent("pointerup"));
      } catch {
        // ignore
      }
    }
    window.removeEventListener("pointerdown", onPointerDownWindow, true);
    rendererDom.removeEventListener("pointermove", onPointerMoveCanvas);
    rendererDom.removeEventListener("pointerleave", onPointerLeaveCanvas);
    if (rendererDom.style.cursor === "grab" || rendererDom.style.cursor === "grabbing") {
      rendererDom.style.cursor = "";
    }
    try {
      disposePositionGizmo(gizmo);
    } catch (error) {
      logger.warn("[PositionGizmo] Failed to dispose gizmo", error);
    }
    // Tell DOM label overlays to hide.
    dispatchPositionGizmoDragEvent("position-gizmo-drag-end", null);
  };

  return { frameUpdate, detach };
}
